let hydrateGeneration = 0;
let activeProductRequest = 0;
let cachedShopOrigin = '';

const guestClickBound = new WeakSet();
const HYDRATE_API_POLL_MAX = 120;

/**
 * Strips non-digit characters from Shopify product IDs.
 * @param {string|number|null|undefined} id
 * @returns {string}
 */
const normalizeId = id => (id ? String(id).replace(/\D+/g, '') : '');

/**
 * @returns {boolean}
 */
const isGuestShopper = () => !window.isLoggedIn;

/**
 * Resolves the storefront origin for absolute product URLs.
 * Canonical link is preferred so preview/dev hosts still emit production URLs.
 * @returns {string}
 */
const getShopOrigin = () => {
  if (cachedShopOrigin) return cachedShopOrigin;

  const canonical = document.querySelector('link[rel="canonical"]')?.href;
  if (canonical) {
    try {
      cachedShopOrigin = new URL(canonical).origin;
      return cachedShopOrigin;
    } catch (e) {}
  }

  const shopUrl = window.customerAccount?.oauthOrigin || window.siteGlobals?.customerAccount?.oauthOrigin;
  if (shopUrl) {
    try {
      cachedShopOrigin = new URL(shopUrl).origin;
    } catch (e) {
      cachedShopOrigin = shopUrl.replace(/\/$/, '');
    }
    return cachedShopOrigin;
  }

  if (window.domain) {
    cachedShopOrigin = `https://${String(window.domain).replace(/^https?:\/\//, '')}`;
    return cachedShopOrigin;
  }

  cachedShopOrigin = window.location.origin;
  return cachedShopOrigin;
};

/**
 * @param {string} url
 * @returns {string}
 */
const toAbsoluteUrl = url => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('//')) return `${window.location.protocol}${url}`;
  if (url.startsWith('/')) return `${getShopOrigin()}${url}`;
  return url;
};

/**
 * @param {string} handle
 * @returns {string}
 */
const buildProductUrl = handle => (handle ? `${getShopOrigin()}/products/${handle}` : '');

/**
 * @param {string|Array<{node?: {url?: string, image?: {url?: string}}, url?: string}>} imageOrImages
 * @returns {string}
 */
export const resolveFeaturedImageUrl = imageOrImages => {
  if (!imageOrImages) return '';
  if (typeof imageOrImages === 'string') return toAbsoluteUrl(imageOrImages);

  if (!Array.isArray(imageOrImages) || !imageOrImages.length) return '';

  const first = imageOrImages[0];
  return toAbsoluteUrl(first?.node?.url || first?.node?.image?.url || first?.url || '');
};

/**
 * Keeps guest PDP URLs aligned with the selected colorway handle.
 * @param {string} handle
 * @returns {boolean} Whether the pathname changed
 */
const syncBrowserProductPath = handle => {
  if (!handle) return false;

  const { pathname, search, hash } = window.location;
  // Only re-align the handle on an actual PDP path. Guest color switches on non-PDP surfaces
  // (closet/outfit-picker, mini-PDP, collection, or page templates) must not rewrite the page URL.
  if (!pathname.includes('/products/')) return false;
  const nextPathname = pathname.replace(/\/products\/[^/?#]+/, `/products/${handle}`);

  if (nextPathname === pathname) return false;

  window.history.replaceState(window.history.state, '', `${nextPathname}${search}${hash}`);

  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', `${window.location.origin}${nextPathname}`);

  return true;
};

/**
 * @param {string} productId
 * @param {string} handle
 * @param {string} productUrl
 * @param {string} [name]
 * @param {string} [imageUrl]
 */
const trackViewedProduct = (productId, handle, productUrl, name = '', imageUrl = '') => {
  if (!productId) return;

  const item = {
    ProductID: productId,
    URL: productUrl || (handle ? buildProductUrl(handle) : window.location.href)
  };

  if (name) item.Name = name;
  if (imageUrl) item.ImageURL = toAbsoluteUrl(imageUrl);

  if (window.klaviyo?.track) window.klaviyo.track('Viewed Product', item);
  else if (window._learnq) window._learnq.push(['track', 'Viewed Product', item]);
};

const orderSelectedVariantFirst = variants => {
  if (!Array.isArray(variants) || variants.length < 2) return variants || [];

  const selectedId = normalizeId(window.__pdpAddButtonPayload?.variantId);
  if (!selectedId) return variants;

  const idx = variants.findIndex(v => normalizeId(v?.id) === selectedId);
  if (idx < 1) return variants;

  const next = variants.slice();
  next.unshift(next.splice(idx, 1)[0]);
  return next;
};

/**
 * Sets customerHub.activeProduct for the selected colorway.
 * Previous colorway fields are not reused when the product ID changes.
 * @param {string} productId
 * @param {string} productUrl
 * @param {string} [imageUrl]
 * @param {string} [productName]
 */
const setActiveProduct = (productId, productUrl, imageUrl = '', productName = '') => {
  if (!window.customerHub || !productId) return;

  const previous = window.customerHub.activeProduct || {};
  const sameProduct = String(previous.id) === String(productId);

  window.customerHub.activeProduct = {
    id: productId,
    link: productUrl || (sameProduct ? previous.link : '') || '',
    name: productName || (sameProduct ? previous.name : '') || '',
    imageUrl: toAbsoluteUrl(imageUrl || (sameProduct ? previous.imageUrl : '') || ''),
    variants: sameProduct ? previous.variants || [] : []
  };
};

/**
 * Invokes Klaviyo Customer Hub hydrateProduct, polling briefly if the API loads late.
 * @param {string} handle
 */
const invokeHydrateProduct = handle => {
  if (!handle) return;

  if (window.customerHubApi?.hydrateProduct) {
    window.customerHubApi.hydrateProduct(handle);
    return;
  }

  let attempts = 0;
  const tick = () => {
    if (window.customerHubApi?.hydrateProduct) {
      window.customerHubApi.hydrateProduct(handle);
      return;
    }
    if (++attempts < HYDRATE_API_POLL_MAX) requestAnimationFrame(tick);
  };

  tick();
};

/**
 * Hydrates customerHub.activeProduct for the selected handle.
 * Uses the passed image immediately, then Hub API or /products/{handle}.js for full data.
 * @param {string} productId
 * @param {string} handle
 * @param {number} generation
 * @param {string} [imageUrl]
 * @param {string} [productName]
 */
const hydrateActiveProductFromHandle = (productId, handle, generation, imageUrl = '', productName = '') => {
  if (!handle) return;

  const productUrl = buildProductUrl(handle);
  const resolvedImage = toAbsoluteUrl(imageUrl);

  setActiveProduct(productId, productUrl, resolvedImage, productName);

  if (window.customerHubApi?.hydrateProduct) {
    window.customerHubApi.hydrateProduct(handle);
    if (resolvedImage) return;
  }

  if (!window.customerHub) return;

  const requestId = ++activeProductRequest;

  fetch(`/products/${handle}.js`)
    .then(res => (res.ok ? res.json() : null))
    .then(product => {
      if (!product || generation !== hydrateGeneration || requestId !== activeProductRequest) return;

      window.customerHub.activeProduct = {
        id: String(product.id),
        link: buildProductUrl(product.handle),
        name: product.title || productName || '',
        imageUrl: toAbsoluteUrl(product.featured_image || product.images?.[0] || resolvedImage || ''),
        variants: orderSelectedVariantFirst(product.variants || [])
      };
    })
    .catch(error => {
      console.warn('[pdpFavorites] Failed to hydrate active product', error);
    });
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} handle
 * @param {string} productUrl
 * @param {string} [imageUrl]
 */
const syncContainerAttributes = (container, productId, handle, productUrl, imageUrl = '') => {
  container.setAttribute('data-fav-pid', productId);

  if (handle) {
    container.setAttribute('data-fav-handle', handle);
    container.setAttribute('data-fav-url', productUrl);
  }

  if (imageUrl) container.setAttribute('data-fav-image', imageUrl);

  const slot = container.querySelector('.klaviyo-wishlist-slot');
  if (slot) {
    slot.setAttribute('data-product-id', productId);
    if (productUrl) slot.setAttribute('data-product-url', productUrl);
  }
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} productUrl
 * @param {boolean} [syncRenderedId]
 */
const applyWidgetProductAttributes = (container, productId, productUrl, syncRenderedId = false) => {
  container.querySelectorAll('.klaviyo-wishlist-slot, .kl-hub-favorites-widget, [data-product-id]').forEach(el => {
    el.setAttribute('data-product-id', productId);
    if (productUrl) el.setAttribute('data-product-url', productUrl);
    if (syncRenderedId) el.setAttribute('data-rendered-product-id', productId);
  });
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} handle
 * @param {string} productUrl
 * @param {string} [imageUrl]
 * @param {boolean} [syncRenderedId]
 */
const syncSlotAttributes = (container, productId, handle, productUrl, imageUrl = '', syncRenderedId = false) => {
  syncContainerAttributes(container, productId, handle, productUrl, imageUrl);
  if (syncRenderedId) applyWidgetProductAttributes(container, productId, productUrl, true);
};

/**
 * @param {string} productId
 * @param {string} productUrl
 * @returns {HTMLDivElement}
 */
const createWishlistSlot = (productId, productUrl) => {
  const slot = document.createElement('div');
  slot.className = 'klaviyo-wishlist-slot';
  slot.setAttribute('data-product-id', productId);
  if (productUrl) slot.setAttribute('data-product-url', productUrl);
  return slot;
};

const FAVORITE_WIDGET_SELECTOR = '.kl-hub-favorites-widget';
const WIDGET_RENDER_CHECK_MS = 1200;
const WIDGET_RENDER_MAX_ATTEMPTS = 3;

const widgetRenderRetries = new WeakMap();

const getFavoriteSlot = container => container.querySelector('.klaviyo-wishlist-slot, [data-product-id]');

const verifyWidgetRendered = (container, productId, productUrl) => {
  setTimeout(() => {
    if (container.getAttribute('data-fav-mounted-pid') !== productId) return;
    if (container.querySelector(FAVORITE_WIDGET_SELECTOR)) {
      widgetRenderRetries.delete(container);
      return;
    }

    const state = widgetRenderRetries.get(container);
    const attempts = state && state.pid === productId ? state.attempts : 0;
    if (attempts >= WIDGET_RENDER_MAX_ATTEMPTS) return;

    widgetRenderRetries.set(container, { pid: productId, attempts: attempts + 1 });
    container.replaceChildren(createWishlistSlot(productId, productUrl));
    invokeHydrateProduct(container.getAttribute('data-fav-handle') || '');
    verifyWidgetRendered(container, productId, productUrl);
  }, WIDGET_RENDER_CHECK_MS);
};

const isKlaviyoHubOpen = () => location.hash.indexOf('k-hub') !== -1 || !!document.querySelector('.kl-hub-drawer--opened, .kl-hub-modal-veil--opened');

const isProductFavorited = productId => {
  const normalized = normalizeId(productId);
  if (!normalized || !window.customerHub) return false;

  const lists = [window.customerHub.favorites, window.customerHub.wishlist, window.customerHub.favoriteProducts, window.customerHub.items];
  return lists.some(list => Array.isArray(list) && list.some(item => normalizeId(item?.id || item?.productId || item?.ProductID) === normalized));
};

const syncWidgetFavoriteVisual = (container, productId) => {
  const icon = container.querySelector('.kl-hub-wishlist-icon');
  if (!icon) return;
  icon.classList.toggle('kl-hub-wishlist-icon--active', isProductFavorited(productId));
};

/**
 * Replaces the wishlist slot so Klaviyo re-binds for guest shoppers on color switch.
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} productUrl
 */
const remountGuestSlot = (container, productId, productUrl) => {
  if (isKlaviyoHubOpen()) {
    container.setAttribute('data-fav-mounted-pid', productId);
    return;
  }

  const hasWidget = !!container.querySelector(FAVORITE_WIDGET_SELECTOR);
  if (hasWidget) {
    container.setAttribute('data-fav-mounted-pid', productId);
    return;
  }

  const mountedPid = container.getAttribute('data-fav-mounted-pid');
  const slot = getFavoriteSlot(container);
  const slotPid = normalizeId(slot?.getAttribute('data-product-id'));

  if (slotPid === productId) {
    container.setAttribute('data-fav-mounted-pid', productId);
    verifyWidgetRendered(container, productId, productUrl);
    return;
  }

  if (mountedPid === productId) {
    verifyWidgetRendered(container, productId, productUrl);
    return;
  }

  container.replaceChildren(createWishlistSlot(productId, productUrl));
  container.setAttribute('data-fav-mounted-pid', productId);
  verifyWidgetRendered(container, productId, productUrl);
};

/**
 * @param {HTMLElement} container
 * @param {string} productId
 * @param {string} handle
 * @param {string} productUrl
 * @param {string} [productName]
 * @param {string} [imageUrl]
 */
const syncFavoriteContext = (container, productId, handle, productUrl, productName = '', imageUrl = '') => {
  const resolvedImage = toAbsoluteUrl(imageUrl);
  syncSlotAttributes(container, productId, handle, productUrl, resolvedImage);
  setActiveProduct(productId, productUrl, resolvedImage, productName);
  if (productName) container.setAttribute('data-fav-name', productName);
  trackViewedProduct(productId, handle, productUrl, productName, resolvedImage);
  if (container.querySelector(FAVORITE_WIDGET_SELECTOR)) syncWidgetFavoriteVisual(container, productId);
};

/**
 * Re-syncs slot attrs and activeProduct immediately before Klaviyo handles the click.
 * @param {HTMLElement} container
 */
const resyncBeforeFavoriteClick = container => {
  const productId = container.getAttribute('data-fav-pid');
  const handle = container.getAttribute('data-fav-handle') || '';
  if (!productId) return;

  const productUrl = container.getAttribute('data-fav-url') || buildProductUrl(handle);
  const imageUrl = container.getAttribute('data-fav-image') || '';
  const productName = container.getAttribute('data-fav-name') || '';

  syncSlotAttributes(container, productId, handle, productUrl, imageUrl, true);
  setActiveProduct(productId, productUrl, imageUrl, productName);

  const activeVariants = window.customerHub?.activeProduct?.variants;
  if (Array.isArray(activeVariants) && activeVariants.length) {
    window.customerHub.activeProduct.variants = orderSelectedVariantFirst(activeVariants);
  }

  invokeHydrateProduct(handle);
  hydrateActiveProductFromHandle(productId, handle, hydrateGeneration, imageUrl, productName);
};

/**
 * @param {HTMLElement} container
 */
const bindGuestClickHandler = container => {
  if (guestClickBound.has(container)) return;

  guestClickBound.add(container);
  container.addEventListener('pointerdown', () => resyncBeforeFavoriteClick(container), true);
};

/**
 * Syncs PDP favorite widgets to the selected colorway product.
 * @param {string|number} productId
 * @param {string} handle
 * @param {string} [productName]
 * @param {boolean} [updateUrl]
 * @param {string|Array} [imageOrImages]
 */
export const syncPdpFavoriteProduct = (productId, handle, productName = '', updateUrl = true, imageOrImages = '') => {
  const normalizedId = normalizeId(productId);
  if (!normalizedId || !handle) return;

  const generation = ++hydrateGeneration;
  const productUrl = buildProductUrl(handle);
  const imageUrl = resolveFeaturedImageUrl(imageOrImages);
  const guest = isGuestShopper();

  if (guest && updateUrl && !isKlaviyoHubOpen()) syncBrowserProductPath(handle);

  document.querySelectorAll('[data-pdp-favorite]').forEach(container => {
    if (guest) remountGuestSlot(container, normalizedId, productUrl);
    bindGuestClickHandler(container);
    syncFavoriteContext(container, normalizedId, handle, productUrl, productName, imageUrl);
  });

  hydrateActiveProductFromHandle(normalizedId, handle, generation, imageUrl, productName);
};
